<script setup lang="ts">
  withDefaults(
    defineProps<{
      /**
       * The tag name of the fragment.
       */
      as?: keyof HTMLElementTagNameMap;
      /**
       * Aligns the fragment to the viewport or the page.
       */
      align?: 'viewport' | 'page';
    }>(),
    {
      as: 'div',
      align: 'viewport',
    },
  );

  const { width: windowWidth } = useWindowSize();
  const bodyWidth = ref(1000);

  const bodyWidthPx = computed(() => `${bodyWidth.value}px`);

  function setWidth() {
    bodyWidth.value = document?.body.clientWidth;
  }

  watch(() => windowWidth.value, useDebounceFn(setWidth, 200));
  watch(() => windowWidth.value, useThrottleFn(setWidth, 200));

  onMounted(() => {
    setWidth();
  });
</script>

<template>
  <component
    :is="as"
    :class="['fragment', `fragment__${align}`]"
  >
    <slot />
  </component>
</template>

<style scoped lang="scss">
  .fragment {
    /*****
    +---------------------viewport--------------------+
    +-------------+---------page--------+-------------+
    |-------------+---------------------+-------------|
    |      A      |           B         |      A      |
    |      A      |           B         |      A      |
    |      A      |           B         |      A      |
    |      A      |           B         |      A      |
    |      A      |           B         |      A      |
    |-------------+---------------------+-------------|
    |<------------|  viewport align
    |             |<---| page align
    *****/
    --a-space: calc((v-bind(bodyWidthPx) - var(--site-content-max-width)) / 2);
    --viewport-margin: calc(-1 * (var(--a-space) + var(--site-content-horizontal-padding)));
    --page-margin: max(0px, var(--a-space));

    &__viewport {
      width: v-bind(bodyWidthPx);
      margin-left: clamp(
        -100vw,
        var(--viewport-margin),
        calc(-1 * var(--site-content-horizontal-padding))
      );
    }

    &__page {
      max-width: var(--site-content-max-width);
      padding-left: var(--site-content-horizontal-padding);
      padding-right: var(--site-content-horizontal-padding);
      margin-left: var(--page-margin);
    }
  }
</style>
